/** Visitors: `body.glide` (straight-line travel to a signalled point,
 * carried on top of it by the push protocol — a current, a conveyor),
 * `ai.patron` (a visit — walk to the first free seat, wait there tagged while
 * an order is open, leave happy or upset when it is done or failed) and
 * `rule.patience` (a meter that drains while its holder waits, shows its mood
 * and walks out when empty). Orders themselves are `rule.ticket`'s. */
import { bool, id, int, list, num, obj } from '../schemaBuilders.js';
import { defineModule, num as read, str, type ModuleCtx } from '../module.js';
import { pushVelocity } from '../physics.js';
import { addTag, removeTag, type EntityV1 } from '../world.js';

export type GlideConfig = { speed: number };

export const glide = defineModule<GlideConfig>({
  id: 'body.glide', version: 1, attach: 'entity', role: 'movement',
  summary: 'Glides in a straight line toward the latest point a behaviour signals (glide), facing its heading; idle once there.',
  config: obj({ speed: num(0.1, 40) }),
  requires: [], events: [], presentation: {},
  proofs: ['glides-at-speed', 'arrives-exactly', 'faces-heading', 'idle-at-rest', 'push-protocol'],
  animStates: () => ['idle', 'move'],
  init(ctx, e) { Object.assign(ctx.data(e), { tx: e.x, ty: e.y, moving: false }); },
  systems: {
    move(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e);
      for (const s of ctx.signals('glide')) {
        if (s.target === e.id && typeof s.data.x === 'number' && typeof s.data.y === 'number') { d.tx = s.data.x; d.ty = s.data.y; }
      }
      const dx = read(d, 'tx') - e.x, dy = read(d, 'ty') - e.y, dist = Math.hypot(dx, dy), step = ctx.config.speed * ctx.dt;
      d.moving = dist > 0;
      if (dist <= step) { e.x = read(d, 'tx'); e.y = read(d, 'ty'); e.vx = 0; e.vy = 0; }
      else {
        e.vx = (dx / dist) * ctx.config.speed; e.vy = (dy / dist) * ctx.config.speed;
        e.x += (dx / dist) * step; e.y += (dy / dist) * step;
        if (Math.abs(dx) > 1e-6) e.facing = dx > 0 ? 1 : -1;
      }
      // Every push about it carries it off its line; it glides back next tick.
      const push = pushVelocity(ctx.signals('push'), e);
      e.x += push.vx * ctx.dt; e.y += push.vy * ctx.dt;
    },
    present(ctx, e) {
      if (e && e.status === 'active') ctx.setAnim(e, ctx.data(e).moving === true ? 'move' : 'idle');
    },
  },
});

export type PatronConfig = {
  /** Where visitors wait (feet points), claimed lowest-free-first. */
  seats: Array<{ x: number; y: number }>;
  /** Where they walk off to (outside the world) before leaving the game. */
  exit: { x: number; y: number };
  /** Tag carried while seated with an order open. */
  waitTag: string;
};

/** Seats held by visitors that have not started to leave. */
function takenSeats(ctx: ModuleCtx<PatronConfig>): Set<number> {
  const taken = new Set<number>();
  for (const other of ctx.entities({ status: 'any' })) {
    const d = ctx.peer(other, 'ai.patron');
    if (d && d.phase !== 'leaving' && typeof d.seat === 'number' && d.seat >= 0) taken.add(d.seat);
  }
  return taken;
}

export const patron = defineModule<PatronConfig>({
  id: 'ai.patron', version: 1, attach: 'entity', role: 'behaviour',
  summary: 'A visit: walks to the first free seat, waits there (tagged) until its order is done or failed, then walks out happy or upset and leaves the game.',
  config: obj({ seats: list(obj({ x: num(-4, 68), y: num(-4, 68) }), 8, 1), exit: obj({ x: num(-8, 72), y: num(-8, 72) }), waitTag: id() }),
  requires: ['body.glide'], events: ['arrive', 'leave'],
  presentation: { arrive: { cue: 'serve' }, leave: { cue: 'none' } },
  proofs: ['walks-to-first-free-seat', 'waits-tagged', 'leaves-on-done', 'leaves-upset-on-failed', 'despawns-at-exit', 'queues-when-seats-full'],
  animStates: () => ['win', 'down'],
  check(c, context, archetype) {
    const errors = archetype && !context.archetypes[archetype]?.modules.includes('body.glide') ? ['ai.patron moves with body.glide on the same kind'] : [];
    // A departing visitor stays in the game for a while (its order settles
    // the tick after it is decided).
    if (c.seats.some(seat => Math.hypot(seat.x - c.exit.x, seat.y - c.exit.y) < 1)) errors.push('the exit must be at least 1 away from every seat');
    return errors;
  },
  init(ctx, e) { Object.assign(ctx.data(e), { seat: -1, phase: 'arriving', mood: '' }); },
  systems: {
    think(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, d = ctx.data(e);
      if (d.phase === 'arriving' && read(d, 'seat') < 0) {
        const taken = takenSeats(ctx);
        const free = c.seats.findIndex((_, i) => !taken.has(i));
        if (free >= 0) d.seat = free;
      }
      const goal = d.phase === 'leaving' ? c.exit : read(d, 'seat') >= 0 ? c.seats[read(d, 'seat')]! : null;
      if (!goal) return;
      if (e.x === goal.x && e.y === goal.y) {
        if (d.phase === 'arriving') {
          d.phase = 'waiting'; addTag(e, c.waitTag);
          ctx.emit('arrive', { x: e.x, y: e.y - e.h / 2, kind: e.archetype });
        } else if (d.phase === 'leaving') { ctx.despawn(e, 'left'); return; }
      }
      ctx.signal('glide', e, null, { x: goal.x, y: goal.y });
    },
    // Flow: after the rules that decide an order this tick.
    flow(ctx, e) {
      if (!e || ctx.data(e).phase === 'leaving') return;
      const d = ctx.data(e);
      const done = ctx.signals('order-done').some(s => s.target === e.id);
      const failed = ctx.signals('order-failed').some(s => s.target === e.id);
      if (!done && !failed) return;
      d.phase = 'leaving'; d.mood = done ? 'happy' : 'upset';
      removeTag(e, ctx.config.waitTag);
      ctx.emit('leave', { x: e.x, y: e.y - e.h / 2, kind: e.archetype, mood: str(d, 'mood')! });
    },
    present(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e);
      if (d.phase === 'leaving') ctx.setAnim(e, d.mood === 'happy' ? 'win' : 'down');
    },
  },
});

export type PatienceConfig = {
  /** Base patience, in ticks. */
  ticks: number;
  /** Named scalar multiplying the base (a difficulty curve; 1 when unset). */
  scaleScalar: string;
  /** Named scalar holding bonus ticks carried to the next arrival. */
  carryScalar: string;
  /** Bonus ticks a perfect order carries to the next arrival. */
  perfectBonusTicks: number;
  /** Drains only while the holder carries this tag. */
  waitTag: string;
  /** Mood thresholds (fraction left): below `impatientBelow` → impatient
   * (`action-1`), below `angryBelow` → angry (`action-2`). */
  impatientBelow: number;
  angryBelow: number;
  /** Draw the remaining patience as the holder's meter. */
  meter: boolean;
};

/** Fraction of patience left in [0, 1] (1 for a holder without the module). */
export function patienceLeft(ctx: { peer(e: EntityV1, id: string): Record<string, unknown> | undefined }, e: EntityV1): number {
  const d = ctx.peer(e, 'rule.patience');
  if (!d || typeof d.left !== 'number' || typeof d.budget !== 'number') return 1;
  return Math.max(0, Math.min(1, d.left / d.budget));
}

export const patience = defineModule<PatienceConfig>({
  id: 'rule.patience', version: 1, attach: 'entity', role: 'rule',
  summary: 'Patience that drains while its holder waits (scaled by a difficulty scalar, topped up by a carried bonus); impatient/angry moods below thresholds; empty, it walks out. A perfect order carries bonus patience to the next arrival.',
  config: obj({ ticks: int(30, 36000), scaleScalar: id(), carryScalar: id(), perfectBonusTicks: int(0, 3600), waitTag: id(),
    impatientBelow: num(0, 1), angryBelow: num(0, 1), meter: bool() }),
  requires: [], events: ['walkout'],
  presentation: { walkout: { cue: 'fail', vfx: { effect: 'shake' } } },
  proofs: ['drains-only-while-waiting', 'scaled-by-scalar', 'carried-bonus-consumed', 'moods-by-threshold', 'walks-out-once-empty', 'perfect-order-carries-bonus', 'meter-shows-left'],
  animStates: () => ['idle', 'action-1', 'action-2'],
  check: c => (c.angryBelow > c.impatientBelow ? ['angryBelow must not exceed impatientBelow'] : []),
  init(ctx, e) {
    const c = ctx.config;
    const budget = Math.max(1, Math.round(c.ticks * ctx.scalar(c.scaleScalar, 1)) + Math.round(ctx.scalar(c.carryScalar, 0)));
    ctx.setScalar(c.carryScalar, 0);
    Object.assign(ctx.data(e), { budget, left: budget, out: false });
  },
  systems: {
    rules(ctx, e) {
      if (!e || e.status !== 'active' || !e.tags.includes(ctx.config.waitTag)) return;
      const d = ctx.data(e);
      d.left = Math.max(0, read(d, 'left') - 1);
      if (read(d, 'left') > 0 || d.out === true) return;
      d.out = true;
      ctx.signal('walkout', e, null, { x: e.x, y: e.y - e.h / 2 });
      ctx.emit('walkout', { x: e.x, y: e.y - e.h / 2, kind: e.archetype });
    },
    flow(ctx, e) {
      if (!e) return;
      if (ctx.signals('order-done').some(s => s.target === e.id && s.data.perfect === true)) {
        ctx.setScalar(ctx.config.carryScalar, ctx.config.perfectBonusTicks);
      }
    },
    present(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, waiting = e.tags.includes(c.waitTag);
      if (c.meter) e.meter = waiting ? { value: patienceLeft(ctx, e), zone: null } : null;
      if (!waiting) return;
      const left = patienceLeft(ctx, e);
      ctx.setAnim(e, left >= c.impatientBelow ? 'idle' : left >= c.angryBelow ? 'action-1' : 'action-2');
    },
  },
});
